import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Handshake, TrendingUp, Megaphone, PackageCheck, GraduationCap, ArrowRight, Send, Store, Truck, Wrench, Building2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import Reveal from '../components/Reveal';
import Marquee from '../components/Marquee';
import { PageHero } from './Products';
import { PARTNER_BENEFITS, PARTNER_TYPES, PARTNER_STEPS, CLIENTS, HERO_IMAGES } from '../mock';
import api, { apiErrorMessage, mediaUrl, asArray } from '../lib/api';

const BENEFIT_ICONS = [TrendingUp, Megaphone, PackageCheck, GraduationCap];
const TYPE_ICONS = [Truck, Store, Wrench, Building2];

export default function Partners() {
  const { toast } = useToast();
  const [form, setForm] = useState({ company: '', name: '', email: '', phone: '', city: '', type: 'Dealer', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  useEffect(() => { api.get('/clients').then((r) => setClients(asArray(r.data))).catch(() => {}); }, []);
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/submissions', {
        type: 'partner', company: form.company, name: form.name, email: form.email,
        phone: form.phone, city: form.city, partnerType: form.type, message: form.message,
      });
      toast({ title: 'Application received!', description: 'Our partnership team will contact you within 2 business days.' });
      setForm({ company: '', name: '', email: '', phone: '', city: '', type: 'Dealer', message: '' });
    } catch (err) {
      toast({ title: 'Could not submit application', description: apiErrorMessage(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#070a12]">
      <PageHero title="Partners" crumb="Partners" image={HERO_IMAGES[2]} />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="section-eyebrow fire-text"><Handshake className="w-4 h-4 text-primary" /> Grow With Us</div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl mt-4 text-white">Become a Closefire Partner</h2>
            <p className="text-gray-400 text-lg mt-5 leading-relaxed">Join a nationwide network of distributors, dealers and service partners delivering certified fire safety solutions. Together, we make India safer — while growing a profitable, future-proof business.</p>
            <div className="flex flex-wrap gap-3 mt-7">
              {PARTNER_STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-2 glass rounded-full px-4 py-2">
                  <span className="grid place-items-center w-6 h-6 rounded-full bg-gradient-to-br from-primary to-accent text-white text-xs font-bold">{i + 1}</span>
                  <span className="text-sm text-gray-200 font-heading font-semibold">{s}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120} className="img-zoom rounded-3xl overflow-hidden grad-border">
            <img src={HERO_IMAGES[1]} alt="Partnership" className="w-full h-[420px] object-cover" />
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-[#05070d]">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center max-w-2xl mx-auto mb-14"><div className="section-eyebrow fire-text justify-center">Partnership Models</div><h2 className="font-heading font-extrabold text-4xl text-white mt-4">Ways to Partner</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PARTNER_TYPES.map((t, i) => {
              const Icon = TYPE_ICONS[i];
              return (
                <Reveal key={t.title} delay={i * 90}>
                  <div className="glass glass-hover rounded-3xl p-7 h-full">
                    <span className="grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent text-white mb-5"><Icon className="w-7 h-7" /></span>
                    <h3 className="font-heading font-bold text-xl text-white">{t.title}</h3>
                    <p className="text-sm text-gray-400 mt-2">{t.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center max-w-2xl mx-auto mb-14"><div className="section-eyebrow fire-text justify-center">Why Partner</div><h2 className="font-heading font-extrabold text-4xl text-white mt-4">Partner Benefits</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PARTNER_BENEFITS.map((b, i) => {
              const Icon = BENEFIT_ICONS[i];
              return (
                <Reveal key={b.title} delay={i * 90} className="text-center">
                  <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent text-white mb-4"><Icon className="w-7 h-7" /></span>
                  <h3 className="font-heading font-bold text-lg text-white">{b.title}</h3>
                  <p className="text-sm text-gray-400 mt-2">{b.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 mb-8"><p className="text-center section-eyebrow text-gray-500 justify-center">Trusted by Industry Leaders</p></div>
        <Marquee items={(clients.length ? clients : CLIENTS.map((c) => ({ name: c })))} renderItem={(c) => (
          c.logo
            ? <img src={mediaUrl(c.logo)} alt={c.name} className="h-14 w-auto object-contain mx-8 opacity-70 grayscale transition duration-300 hover:opacity-100 hover:grayscale-0 hover:scale-110 hover:drop-shadow-[0_0_16px_rgba(232,51,42,0.5)]" />
            : <span className="font-heading font-semibold text-lg text-gray-300 whitespace-nowrap px-4">{c.name}</span>
        )} />
      </section>

      {/* Become a partner form */}
      <section className="py-24 bg-[#05070d]">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal className="text-center mb-10"><div className="section-eyebrow fire-text justify-center">Apply Now</div><h2 className="font-heading font-extrabold text-4xl text-white mt-4">Become a Partner</h2><p className="text-gray-400 mt-3">Fill in your details and our team will reach out to discuss the opportunity.</p></Reveal>
          <Reveal delay={120}>
            <form onSubmit={submit} className="glass rounded-3xl p-8 grid sm:grid-cols-2 gap-4">
              <Input name="company" value={form.company} onChange={change} required placeholder="Company / Firm name" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
              <Input name="name" value={form.name} onChange={change} required placeholder="Contact person" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
              <Input name="email" type="email" value={form.email} onChange={change} required placeholder="Email" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
              <Input name="phone" value={form.phone} onChange={change} required placeholder="Phone" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
              <Input name="city" value={form.city} onChange={change} required placeholder="City / Territory" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
              <select name="type" value={form.type} onChange={change} className="h-12 rounded-lg bg-white/5 border border-white/10 text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                {['Distributor', 'Dealer', 'Service Partner', 'OEM / Institutional'].map((o) => <option key={o} value={o} className="bg-[#0b0f1a]">{o}</option>)}
              </select>
              <Textarea name="message" value={form.message} onChange={change} placeholder="Tell us about your business" rows={4} className="sm:col-span-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none" />
              <Button type="submit" disabled={submitting} size="lg" className="sm:col-span-2 gap-2 h-12 bg-gradient-to-r from-primary to-accent hover:opacity-90">{submitting ? 'Submitting…' : <>Submit Application <Send className="w-4 h-4" /></>}</Button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
